import * as duckdb from '@duckdb/duckdb-wasm'

import {
  Field,
  Float64,
  List,
  tableFromArrays,
  vectorFromArray,
  Utf8,
  Table,
} from 'apache-arrow'

import { MANUAL_BUNDLES } from './utils/const'
import { DuckDBSchemaType } from './utils/types'

type InitOptions = {
  logger?: boolean;
  onProgress?: (p: duckdb.InstantiationProgress) => void;
};
class Mutex {
  private chain = Promise.resolve()
  run<T>(fn: () => Promise<T>): Promise<T> {
    const res = this.chain.then(fn, fn)
    this.chain = res.then(() => undefined, () => undefined)
    return res
  }
}

export class DuckDBService {
  private static _instance: DuckDBService | null = null

  static instance(): DuckDBService {
    if (!this._instance) this._instance = new DuckDBService()
    return this._instance
  }

  private initPromise: Promise<void> | null = null
  private db: duckdb.AsyncDuckDB | null = null
  private conn: duckdb.AsyncDuckDBConnection | null = null

  private worker: Worker | null = null

  private mutex = new Mutex()

  // initialize (callable multiple times)
  init(opts: InitOptions = {}) {
    if (this.initPromise) return this.initPromise
    this.initPromise = (async () => {
      const logger = opts.logger ? new duckdb.ConsoleLogger() : new duckdb.VoidLogger()
      // select a bundle based on browser checks
      const bundle = await duckdb.selectBundle(MANUAL_BUNDLES)
      // create worker and database
      this.worker = new Worker(bundle.mainWorker!, { type: 'module' })
      this.db = new duckdb.AsyncDuckDB(logger, this.worker)
      // instantiate duckdb-wasm instance
      await this.db.instantiate(bundle.mainModule, bundle.pthreadWorker, opts.onProgress)
      // connect to the database
      this.conn = await this.db.connect()
    })().catch(e => {
      // if init failed, allow to try again
      this.initPromise = null
      throw e
    })
    return this.initPromise
  }

  // get connection
  async connection() {
    await this.init()
    if (!this.conn) throw new Error('DuckDB connection is not available')
    return this.conn
  }

  private tableToObjects<T extends Record<string, any>>(table: Table) {
    const result: T[] = []
    const fields = table.schema.fields.map(f => f.name)
    const cols = fields.map(name => table.getChild(name))
    const length = table.numRows
    for (let i = 0; i < length; i++) {
      const obj: any = {}
      for (let c = 0; c < fields.length; c++) {
        obj[fields[c]] = cols[c]?.get(i)
      }
      result.push(obj as T)
    }
    return result
  }

  // safe query: serialize the query and try to reset the database if it fails
  async query<T extends Record<string, any> = any>(sql: string) {
    return this.mutex.run(async () => {
      try {
        const conn = await this.connection()
        const table = await conn.query<T>(sql)
        const objects = this.tableToObjects<T>(table)
        return objects
      } catch {
        // sometimes calls on a killed worker can behave badly, so do a hard reset
        await this.resetLocked()
        const conn = await this.connection()
        const table = await conn.query<T>(sql)
        const objects = this.tableToObjects<T>(table)
        return objects
      }
    })
  }

  async queryOne<T extends Record<string, any> = any>(sql: string) {
    const result = await this.query<T>(sql)
    return result?.[0]
  }

  async createTable(
    tableName: string,
    schema: Record<string, DuckDBSchemaType>,
  ) {
    await this.query(`DROP TABLE IF EXISTS ${tableName}`)
    await this.query(`CREATE TABLE ${tableName} (${Object.entries(schema)
      .map(([key, value]) => `${key} ${value}`)
      .join(', ')})`)
  }

  async dropTable(tableName: string) {
    await this.query(`DROP TABLE IF EXISTS ${tableName}`)
  }

  async pushData<T extends Record<string, any>>(
    tableName: string,
    data: T[],
    schema: Record<string, DuckDBSchemaType>,
  ) {
    let table: ReturnType<typeof tableFromArrays>
    try {
      const cols = Object.keys(schema)
      const arrays: Record<string, any> = {}
      for (const col of cols) {
        switch (schema[col]) {
          case 'DATE':
          case 'VARCHAR': {
            const listData = data.map(item => item[col] ?? null)
            arrays[col] = vectorFromArray(listData, new Utf8())
            break
          }
          case 'DOUBLE': {
            const listData = data.map(item => item[col] ?? null)
            arrays[col] = vectorFromArray(listData, new Float64())
            break
          }
          case 'VARCHAR[]': {
            const listType = new List(new Field('item', new Utf8(), true))
            const listData = data.map(item => {
              const value = item[col]
              if (!value) return []
              if (Array.isArray(value))
                return value.map(item => (item ? String(item) : null))
              return [String(value)]
            })
            arrays[col] = vectorFromArray(listData, listType)
            break
          }
          default:
            throw new Error(`Unknown schema: ${schema[col]}`)
        }
      }
      table = tableFromArrays(arrays)
    } catch (error) {
      console.error(error)
      throw error
    }
    return this.mutex.run(async () => {
      try {
        const conn = await this.connection()
        return await conn.insertArrowTable(table, {
          name: tableName,
          create: false,
        })
      } catch {
        await this.resetLocked()
        const conn = await this.connection()
        return await conn.insertArrowTable(table, {
          name: tableName,
          create: false,
        })

      }
    })
  }

  private async closeLocked(): Promise<void> {
    try {
      await (this.conn as any)?.close?.()
    } finally {
      this.conn = null
      await (this.db as any)?.terminate?.()
      this.db = null
      this.worker?.terminate()
      this.worker = null
      this.initPromise = null
    }
  }

  private async resetLocked(): Promise<void> {
    await this.closeLocked()
    await this.init()
  }

  async close(): Promise<void> {
    await this.mutex.run(() => this.closeLocked())
  }

  async reset(): Promise<void> {
    await this.mutex.run(() => this.resetLocked())
  }
}
